import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { getIsUngatedForSeats } from 'pricing-pages-lib/utils/gatingUtils';
import { sortApiNamesByProductLevel } from 'pricing-pages-lib/utils/sortingUtils';
import { useMemo } from 'react';
import { buildFilteredHighlightedProducts, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { useAppSelector } from '../../../reducers';
import { isBundleListWithConsistentTypeSelector } from '../../../selectors/ProductsSelectors';
import { usePricingLibSelector } from '../../../selectors/SelectorUtils';
import ProductIcon from '../../common/ProductIcon';
import { useErrorBoundary } from '../../error-boundary/useErrorBoundary';
import withErrorBoundary from '../../error-boundary/withErrorBoundary';
import GenericBundleFeatureTableHeaderCell from './GenericBundleFeatureTableHeaderCell';
import { usePinnedFeaturesByProductLine } from '../../../hooks/useFeatures';
import { isUngatedForInbound25Selector } from '../../../selectors/GatingSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LabelContent = ({
  productLine
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(ProductIcon, {
      name: productLine,
      iconStyle: {
        inlineSize: '24px',
        marginInlineEnd: '300'
      }
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-300",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "suiteFeatureTable.section.title",
        options: {
          product: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `productLine.${productLine}`
          })
        }
      })
    })]
  });
};
const buildFeatureTableHeaderData = (apiNames, screen, trackingSource) => {
  const sortedApiNames = sortApiNamesByProductLevel(apiNames);
  return apiNames.reduce((headerDataMap, apiName, index) => Object.assign({}, headerDataMap, {
    [apiName]: {
      Content: /*#__PURE__*/_jsx(GenericBundleFeatureTableHeaderCell, {
        apiName: sortedApiNames[index],
        screen: screen,
        trackingSource: trackingSource
      }, apiName)
    }
  }), {});
};
const buildFeatureTableBodyData = (features, productLevelsFromBundle, productLinesFromBundle, screen) => {
  const sectionData = productLinesFromBundle.reduce((sectionDataMap, productLine) => Object.assign({}, sectionDataMap, {
    [productLine]: {
      features: features[productLine] || [],
      LabelContent: /*#__PURE__*/_jsx(LabelContent, {
        productLine: productLine
      }),
      apiNames: buildFilteredHighlightedProducts({
        productLine,
        productLevels: productLevelsFromBundle
      })
    }
  }), {});
  if (Object.keys(sectionData).length > 0) {
    return {
      sharedData: {
        subscreen: screen
      },
      sectionData
    };
  }
};
const GenericBundleFeatureTable = ({
  apiNames,
  productLinesFromBundle,
  screen,
  trackingSource
}) => {
  const {
    showBoundary
  } = useErrorBoundary();
  const isUngatedForInbound25 = useAppSelector(isUngatedForInbound25Selector);
  const isSeatsRedesignUngated = usePricingLibSelector(getIsUngatedForSeats);
  const productLevelsFromBundle = useMemo(() => apiNames.map(apiName => {
    const level = getProductLevelFromApiNameString(apiName);
    return level;
  }), [apiNames]);
  if (!productLevelsFromBundle) {
    showBoundary(new CustomError('Invalid product lines', {
      productLinesFromBundle,
      trackingSource
    }));
  }
  const productLineToApiName = productLinesFromBundle.reduce((acc, productLine) => {
    acc[productLine] = buildFilteredHighlightedProducts({
      productLine,
      productLevels: productLevelsFromBundle
    });
    return acc;
  }, {});
  const {
    featuresByProductLine,
    loading,
    error
  } = usePinnedFeaturesByProductLine(productLineToApiName);
  const isBundleWithConsistentType = useAppSelector(state => {
    return isBundleListWithConsistentTypeSelector(state, {
      apiNames
    });
  });
  if (!isBundleWithConsistentType) {
    showBoundary(new CustomError('Bundle type is not consistent', {
      isBundleWithConsistentType,
      trackingSource
    }));
  }
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError('Error fetching features', {
      error,
      productLevelsFromBundle,
      apiNames,
      productLineToApiName,
      trackingSource
    }));
  }
  if (!featuresByProductLine || !Object.keys(featuresByProductLine).length) {
    return null;
  }
  const headerData = buildFeatureTableHeaderData(apiNames, screen, trackingSource);
  const bodyData = buildFeatureTableBodyData(featuresByProductLine, productLevelsFromBundle, productLinesFromBundle, screen);
  if (!bodyData) {
    return null;
  }
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: headerData,
    bodyData: bodyData,
    canViewNewSeats: isSeatsRedesignUngated,
    isInbound25PricingPage: isUngatedForInbound25
  });
};
export default withErrorBoundary(GenericBundleFeatureTable, {
  fallback: null,
  errorBoundaryName: 'GENERIC_BUNDLE_FEATURE_TABLE'
});